import { CHOCOBO_BEHAVIORS, CHOCOBO_BASE_STATS } from '@/constants/chocoboConstants';
import { chocoboImages } from '../utils/imageImports';
import { EffectManager } from '@/services/effectManager';
import { AgingSystem } from '@/services/agingSystem';

export const TRAINING_TYPES = {
  SPEED: 'speed',
  STAMINA: 'stamina',
  BEHAVIOR: 'behavior'
};

export class Chocobo {
  constructor(color, region, name = '') {
    if (!CHOCOBO_BASE_STATS[color] || !CHOCOBO_BASE_STATS[color][region]) {
      throw new Error(`Invalid color ${color} or region ${region} combination`)
    }
  
    this.id = crypto.randomUUID()
    this.name = name
    this.color = color
    this.region = region
    this.experience = 0
    this.level = 1
    this.gender = Math.random() < 0.5 ? 'male' : 'female'
    this.birthDay = 1 // Will be set properly when created in game context
    this.estimatedBirthDay = null // For wild chocobos with unknown birth dates
    
    const baseStats = CHOCOBO_BASE_STATS[color][region]
    this.behaviorId = baseStats.base_behaviour || 3 // Default to STEADY if missing
    this.xFactorPercentage = baseStats.xfactor_percentage || 1
  
    // Initialize base stats
    this.baseSpeed = baseStats.base_speed
    this.baseStamina = baseStats.base_stamina

    // Generate initial bonus stats (1-40)
    this.speedBonus = Math.floor(Math.random() * 40) + 1
    this.staminaBonus = Math.floor(Math.random() * 40) + 1
    
    // Apply X-Factor multiplier to initial bonus only if they have the X-Factor
    this.hasSpeedXFactor = (Math.random() * 100) < this.xFactorPercentage
    this.hasStaminaXFactor = (Math.random() * 100) < this.xFactorPercentage
    
    if (this.hasSpeedXFactor) this.speedBonus *= 2
    if (this.hasStaminaXFactor) this.staminaBonus *= 2
  
    // Store initial bonuses for reference
    this.initialSpeedBonus = this.speedBonus
    this.initialStaminaBonus = this.staminaBonus
    
    // Calculate total stats (base + bonus)
    this.speed = this.baseSpeed + this.speedBonus
    this.stamina = this.baseStamina + this.staminaBonus
    
    // Calculate total stats
    this.updateTotalStats()
  
    // Training tracking
    this.consecutiveBehaviorTraining = 0
    this.trainingType = TRAINING_TYPES.SPEED
    
    // Assign image based on color
    this.imageUrl = chocoboImages[this.color] || chocoboImages.yellow
    this.raceHistory = []
    this.recentForm = Array(5).fill(null)
    this.personalBests = {}
    
    // Initialize career summary
    this.careerSummary = {
      careerEarnings: 0,
      careerWins: 0,
      careerRaces: 0,
      firstRaceDay: null,
      lastRaceDay: null
    }
    
    // Breeding state properties
    this.isBreeding = false
    this.breedingRole = null // 'mother' or 'father'
    this.breedingStartDay = null
    this.breedingEndDay = null
    this.breedingPartnerId = null
    this.breedingPartnerName = null
    this.offspringData = null // Store pre-generated offspring data
    
    // Lineage tracking
    this.motherId = null
    this.motherName = null
    this.motherColor = null
    this.motherStats = null // { speed, stamina, level, wins, earnings }
    this.fatherId = null
    this.fatherName = null
    this.fatherColor = null
    this.fatherStats = null // { speed, stamina, level, wins, earnings }
    
    // Sale preferences
    this.notForSale = false // Player can mark chocobo as not for sale to prevent NPC bids
    
    // Transit state
    this.inTransit = false // Whether chocobo is currently on transit mission
    
    // Equipment and Effects System
    this.equipment = {
      charm: null // Equipped charm item
    }
    this.activeEffects = [] // Temporary effects from consumables
    this.consumedBreedingItems = [] // Breeding enhancement items used

  }

  get behavior() {
    const behavior = Object.values(CHOCOBO_BEHAVIORS).find(b => b.id === this.behaviorId)
    
    // Safety fallback: if no behavior found, use STEADY (id: 3) as default
    if (!behavior) {
      console.warn(`Chocobo ${this.name} has invalid behaviorId ${this.behaviorId}, using STEADY as fallback`)
      return Object.values(CHOCOBO_BEHAVIORS).find(b => b.id === 3) || {
        id: 3,
        name: 'STEADY',
        description: 'Steady',
        improvement_factor: 5
      }
    }
    
    return behavior
  }

  get improvementRate() {
    return this.behavior.improvement_factor
  }

  get maxLevel() {
    return 40
  }

  getTotalGains() {
    return {
      speed: this.speedBonus - this.initialSpeedBonus,
      stamina: this.staminaBonus - this.initialStaminaBonus
    }
  }

  gainExperience(amount) {
    if (this.level >= this.maxLevel) return false
    
    const oldLevel = this.level
    this.experience += amount
    
    // Calculate new level with scaling XP requirements
    let requiredXp = 0
    let newLevel = 1
    
    while (newLevel < this.maxLevel && this.experience >= requiredXp + this.getXpRequirementForLevel(newLevel)) {
      requiredXp += this.getXpRequirementForLevel(newLevel)
      newLevel++
    }
    
    newLevel = Math.min(this.maxLevel, newLevel)
    
    // Check if level actually increased
    if (newLevel > oldLevel) {
      const improvements = []
      for (let level = oldLevel + 1; level <= newLevel; level++) {
        improvements.push(this.processLevelUp())
      }
      this.level = newLevel
      
      // Return combined improvements WITH level information
      return {
        speedGain: improvements.reduce((acc, curr) => acc + curr.speedGain, 0),
        staminaGain: improvements.reduce((acc, curr) => acc + curr.staminaGain, 0),
        behaviorImproved: improvements.some(imp => imp.behaviorImproved),
        oldLevel,
        newLevel,
        trainingFocus: this.trainingType
      }
    }
    
    return false
  }
  
  getXpRequirementForLevel(level) {
    // Scaling XP: base 100 + 15 per level (so level 10 needs 250 XP, level 20 needs 400 XP)
    return 100 + (level * 15)
  }
  
  getTotalXpRequiredForLevel(targetLevel) {
    let total = 0
    for (let i = 1; i < targetLevel; i++) {
      total += this.getXpRequirementForLevel(i)
    }
    return total
  }
  
  getXpToNextLevel() {
    if (this.level >= this.maxLevel) return 0
    
    const currentLevelXpTotal = this.getTotalXpRequiredForLevel(this.level)
    const nextLevelXpRequirement = this.getXpRequirementForLevel(this.level + 1)
    const xpIntoCurrentLevel = this.experience - currentLevelXpTotal
    
    return nextLevelXpRequirement - xpIntoCurrentLevel
  }
  
  startBreeding(partnerId, partnerName, currentDay, role = 'mother') {
    this.isBreeding = true
    this.breedingRole = role
    this.breedingStartDay = currentDay
    this.breedingEndDay = currentDay + 14
    this.breedingPartnerId = partnerId
    this.breedingPartnerName = partnerName
  }
  
  completeBreeding() {
    this.isBreeding = false
    this.breedingRole = null
    this.breedingStartDay = null
    this.breedingEndDay = null
    this.breedingPartnerId = null
    this.breedingPartnerName = null
    this.offspringData = null
  }
  
  getBreedingDaysRemaining(currentDay) {
    if (!this.isBreeding || !this.breedingEndDay) return 0
    return Math.max(0, this.breedingEndDay - currentDay)
  }
  
  getBreedingStage(currentDay) {
    if (!this.isBreeding || !this.breedingStartDay) return null
    
    const daysElapsed = currentDay - this.breedingStartDay
    
    if (daysElapsed <= 6) return 'incubating'
    else if (daysElapsed <= 9) return 'hatching'
    else if (daysElapsed <= 14) return 'nursing'
    else return 'complete'
  }
  
  setLineage(mother, father) {
    // Store mother's information
    if (mother) {
      this.motherId = mother.id
      this.motherName = mother.name
      this.motherColor = mother.color
      this.motherStats = {
        speed: mother.speed,
        stamina: mother.stamina,
        level: mother.level,
        wins: this.calculateWins(mother),
        earnings: mother.careerSummary?.careerEarnings || 0
      }
    }
    
    // Store father's information
    if (father) {
      this.fatherId = father.id
      this.fatherName = father.name
      this.fatherColor = father.color
      this.fatherStats = {
        speed: father.speed,
        stamina: father.stamina,
        level: father.level,
        wins: this.calculateWins(father),
        earnings: father.careerSummary?.careerEarnings || 0
      }
    }
  }
  
  calculateWins(chocobo) {
    if (!chocobo.raceHistory) return 0
    return chocobo.raceHistory.filter(race => race.position === 1).length
  }
  
  hasLineageInfo() {
    return !!(this.motherId || this.fatherId)
  }
  
  getLineageSummary() {
    if (!this.hasLineageInfo()) {
      return "Wild-caught chocobo - lineage unknown"
    }
    
    const mother = this.motherName ? this.motherName : "Unknown"
    const father = this.fatherName ? this.fatherName : "Unknown"
    
    if (this.motherName && this.fatherName) {
      return `Bred from ${mother} and ${father}`
    } else if (this.motherName) {
      return `Bred from ${mother} and an unknown father`
    } else if (this.fatherName) {
      return `Bred from an unknown mother and ${father}`
    }
    
    return "Lineage partially known"
  }


  processLevelUp() {
    const improvements = {
      speedGain: 0,
      staminaGain: 0,
      behaviorImproved: false
    }
  
    if (this.trainingType === TRAINING_TYPES.SPEED) {
      if (Math.random() < 0.6) {
        const gain = 1
        this.speedBonus += gain
        improvements.speedGain = gain
      }
      if (Math.random() < 0.2) {
        const gain = 1
        this.staminaBonus += gain
        improvements.staminaGain = gain
      }
    } 

    else if (this.trainingType === TRAINING_TYPES.STAMINA) {
      if (Math.random() < 0.6) {
        const gain = 1
        this.staminaBonus += gain
        improvements.staminaGain = gain
      }
      if (Math.random() < 0.2) {
        const gain = this.hasSpeedXFactor ? 2 : 1
        this.speedBonus += gain
        improvements.speedGain = gain
      }
    }
    else if (this.trainingType === TRAINING_TYPES.BEHAVIOR) {
      if (Math.random() < 0.2) {
        const gain = 1
        this.speedBonus += gain
        improvements.speedGain = gain
      }
      if (Math.random() < 0.2) {
        const gain = 1
        this.staminaBonus += gain
        improvements.staminaGain = gain
      }
      
      this.consecutiveBehaviorTraining++
      if (this.consecutiveBehaviorTraining >= 6 && this.behaviorId > 1) {
        this.behaviorId--
        this.consecutiveBehaviorTraining = 0
        improvements.behaviorImproved = true
        
        // Auto-switch to speed training when behavior reaches maximum (level 1)
        if (this.behaviorId === 1) {
          this.setTrainingFocus(TRAINING_TYPES.SPEED)
        }
      }
    }

    // Update total stats with accumulated bonuses
    this.updateTotalStats()
    
    return improvements
  }

  setTrainingFocus(type) {
    if (type !== this.trainingType) {
      this.trainingType = type
      // Reset behavior training counter if switching away from behavior training
      if (type !== TRAINING_TYPES.BEHAVIOR) {
        this.consecutiveBehaviorTraining = 0
      }
    }
  }

  train(type, currentDay = null) {
    if (this.level >= this.maxLevel) return null

    // Track consecutive behavior training
    if (type === TRAINING_TYPES.BEHAVIOR && this.behaviorId > 1) {
      this.consecutiveBehaviorTraining++
    } else if (type === TRAINING_TYPES.BEHAVIOR && this.behaviorId == 1) {
      this.setTrainingFocus('speed')
    } else {
      this.consecutiveBehaviorTraining = 0
    }

    // Store current training type
    this.trainingType = type

    // Base experience gain (can be modified based on factors)
    const baseExperience = Math.floor(Math.random() * 20) + 10
    
    // Apply effect multipliers if currentDay is provided
    let finalExperience = baseExperience
    if (currentDay !== null) {
      const xpMultiplier = EffectManager.calculateXPMultiplier(this, currentDay)
      const trainingMultiplier = EffectManager.calculateTrainingMultiplier(this, type, currentDay)
      
      // Apply age-based training modifier
      const ageTrainingModifier = this.getAgeTrainingModifier(currentDay)
      
      finalExperience = Math.floor(baseExperience * xpMultiplier * trainingMultiplier * ageTrainingModifier)
    }

    // Process level up and get results
    const levelUpResult = this.gainExperience(finalExperience)

    return {
      levelUp: levelUpResult !== false,
      speedGain: levelUpResult ? levelUpResult.speedGain : 0,
      staminaGain: levelUpResult ? levelUpResult.staminaGain : 0,
      behaviorImproved: levelUpResult ? levelUpResult.behaviorImproved : false,
      experienceGained: finalExperience,
      baseExperience: baseExperience,
      effectMultiplier: currentDay !== null ? (finalExperience / baseExperience) : 1.0,
      ageTrainingModifier: currentDay !== null ? this.getAgeTrainingModifier(currentDay) : 1.0
    }
  }

  updateTotalStats() {
    this.speed = this.baseSpeed + this.speedBonus
    this.stamina = this.baseStamina + this.staminaBonus
  }

  recordRaceResult(position, circuit, raceType, finishTime, day, earnings = 0) {
    this.raceHistory.push({
      position,
      circuit,
      raceType,
      finishTime,
      day,
      level: this.level,
      speed: this.speed,
      stamina: this.stamina,
      behavior: this.behaviorId,
      earnings: earnings
    })
  
    this.recentForm = [...this.recentForm.slice(-4), position].slice(-5)
    
    // Initialize career summary if it doesn't exist (for backward compatibility)
    if (!this.careerSummary) {
      this.careerSummary = {
        careerEarnings: 0,
        careerWins: 0,
        careerRaces: 0,
        firstRaceDay: day,
        lastRaceDay: day
      }
    }
    
    // Set first race day if this is the first race
    if (this.careerSummary.firstRaceDay === null) {
      this.careerSummary.firstRaceDay = day
    }
    
    // Update career summary
    this.careerSummary.careerEarnings += earnings
    this.careerSummary.careerRaces++
    this.careerSummary.lastRaceDay = day
    
    if (position === 1) {
      this.careerSummary.careerWins++
    }
  }

  // Age-related methods
  getAge(currentDay) {
    return AgingSystem.getAgeInDays(this, currentDay)
  }

  getAgeInMonths(currentDay) {
    return AgingSystem.getAgeInMonths(this, currentDay)
  }

  getFormattedAge(currentDay, showApproximate = true) {
    return AgingSystem.getFormattedAge(this, currentDay, showApproximate)
  }

  getAgePerformanceModifier(currentDay) {
    return AgingSystem.getAgePerformanceModifier(this, currentDay)
  }

  isInPrime(currentDay) {
    return AgingSystem.isInPrime(this, currentDay)
  }

  getAgeCategory(currentDay) {
    return AgingSystem.getAgeCategory(this, currentDay)
  }

  // Initialize estimated birth day for wild chocobos
  initializeEstimatedAge(currentDay) {
    if (!this.birthDay || this.birthDay <= 0) {
      this.estimatedBirthDay = AgingSystem.generateEstimatedBirthDay(this, currentDay)
    }
  }

  // Get age-modified effective stats for racing/training
  getEffectiveSpeed(currentDay) {
    const ageModifier = this.getAgePerformanceModifier(currentDay)
    return Math.floor(this.speed * ageModifier)
  }

  getEffectiveStamina(currentDay) {
    const ageModifier = this.getAgePerformanceModifier(currentDay)
    return Math.floor(this.stamina * ageModifier)
  }

  // Get age-based training effectiveness modifier
  getAgeTrainingModifier(currentDay) {
    return AgingSystem.getAgeTrainingModifier(this, currentDay)
  }

  // Get both base and age-modified stats for display
  getStatsWithAge(currentDay) {
    const ageModifier = this.getAgePerformanceModifier(currentDay)
    return {
      baseSpeed: this.speed,
      baseStamina: this.stamina,
      effectiveSpeed: this.getEffectiveSpeed(currentDay),
      effectiveStamina: this.getEffectiveStamina(currentDay),
      ageModifier: ageModifier,
      isAtPeak: Math.abs(ageModifier - 1.0) < 0.01,
      isDeclined: ageModifier < 0.95
    }
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      color: this.color,
      region: this.region,
      level: this.level,
      experience: this.experience,
      gender: this.gender,
      birthDay: this.birthDay,
      estimatedBirthDay: this.estimatedBirthDay,
      speed: this.speed,
      stamina: this.stamina,
      behaviorId: this.behaviorId,
      xFactorPercentage: this.xFactorPercentage,
      hasSpeedXFactor: this.hasSpeedXFactor,
      hasStaminaXFactor: this.hasStaminaXFactor,
      trainingType: this.trainingType,
      consecutiveBehaviorTraining: this.consecutiveBehaviorTraining,
      baseSpeed: this.baseSpeed,
      baseStamina: this.baseStamina,
      speedBonus: this.speedBonus,
      staminaBonus: this.staminaBonus,
      initialSpeedBonus: this.initialSpeedBonus,
      initialStaminaBonus: this.initialStaminaBonus,
      imageUrl: this.imageUrl,
      personalBests: this.personalBests,
      raceHistory: this.raceHistory.map(race => ({
        position: race.position,
        circuit: race.circuit,
        raceType: race.raceType,
        finishTime: race.finishTime,
        day: race.day,
        level: race.level,
        speed: race.speed,
        stamina: race.stamina,
        behavior: race.behavior,
        earnings: race.earnings || 0
      })),
      recentForm: this.recentForm,
      // Career tracking
      careerSummary: this.careerSummary,
      // Breeding state
      isBreeding: this.isBreeding,
      breedingRole: this.breedingRole,
      breedingStartDay: this.breedingStartDay,
      breedingEndDay: this.breedingEndDay,
      breedingPartnerId: this.breedingPartnerId,
      breedingPartnerName: this.breedingPartnerName,
      offspringData: this.offspringData,
      // Lineage data
      motherId: this.motherId,
      motherName: this.motherName,
      motherColor: this.motherColor,
      motherStats: this.motherStats,
      fatherId: this.fatherId,
      fatherName: this.fatherName,
      fatherColor: this.fatherColor,
      fatherStats: this.fatherStats,
      // Sale preferences
      notForSale: this.notForSale,
      // Transit state
      inTransit: this.inTransit
    }
  }
}
